import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  ButtonGroup,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../../helpers/axiosInstance";
import "./dashboard.scss";

const RevenueAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [timeframe, setTimeframe] = useState("monthly");

  // New state for custom date modal
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    fetchRevenueData();
  }, [timeframe]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      
      // Prepare query parameters
      const params = { timeframe };
      if (timeframe === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const response = await axiosInstance.get(`/api/v1/admin/revenue-analytics`, { params });
      setRevenueData(response.data.data);
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      // Validate date range
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      if (start > end) {
        alert("Start date must be before or equal to end date");
        return;
      }

      // Set timeframe to custom
      setTimeframe('custom');

      // Close the modal
      setIsCustomDateModalOpen(false);

      // Explicitly call fetchRevenueData to hit the API
      fetchRevenueData();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getChartOptions = () => {
    if (!revenueData?.chartData) return {};

    return {
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        width: [0, 3],
        curve: "smooth",
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "20%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: "bottom",
      },
      colors: ["#5664d2", "#1cbb8c"],
      labels: revenueData.chartData.labels,
      xaxis: {
        type: "category",
      },
      yaxis: {
        labels: {
          formatter: (value) => formatCurrency(value),
        },
      },
      tooltip: {
        y: {
          formatter: (value) => formatCurrency(value),
        },
      },
    };
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="card-title mb-0">Revenue Analytics</h4>
            <ButtonGroup size="sm">
              <Button
                color={timeframe === "daily" ? "primary" : "light"}
                onClick={() => setTimeframe("daily")}
              >
                Daily
              </Button>
              <Button
                color={timeframe === "monthly" ? "primary" : "light"}
                onClick={() => setTimeframe("monthly")}
              >
                Monthly
              </Button>
              <Button
                color={timeframe === "yearly" ? "primary" : "light"}
                onClick={() => setTimeframe("yearly")}
              >
                Yearly
              </Button>
              <Button
                color={timeframe === "custom" ? "primary" : "light"}
                onClick={() => setIsCustomDateModalOpen(true)}
              >
                Custom
              </Button>
            </ButtonGroup>
          </div>

          {/* Custom Date Modal */}
          <Modal 
            isOpen={isCustomDateModalOpen} 
            toggle={() => setIsCustomDateModalOpen(!isCustomDateModalOpen)}
          >
            <ModalHeader toggle={() => setIsCustomDateModalOpen(false)}>
              Select Custom Date Range
            </ModalHeader>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label for="startDate">Start Date</Label>
                  <Input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="endDate">End Date</Label>
                  <Input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={handleCustomDateSubmit}>
                Apply
              </Button>{' '}
              <Button color="secondary" onClick={() => setIsCustomDateModalOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          <div className="mt-3">
            <div id="line-column-chart" className="apex-charts" dir="ltr">
              <ReactApexChart
                options={getChartOptions()}
                series={revenueData?.chartData?.series || []}
                type="line"
                height={280}
              />
            </div>
          </div>
        </CardBody>

        <CardBody className="border-top">
          <Row>
            <Col sm={4}>
              <div className="text-center">
                <h5 className="mb-0 font-size-20">
                  {formatCurrency(
                    revenueData?.revenueData?.monthlyRevenue || 0
                  )}
                </h5>
                <p className="text-muted">Monthly Revenue</p>
              </div>
            </Col>

            <Col sm={4}>
              <div className="text-center">
                <h5 className="mb-0 font-size-20">
                  {formatCurrency(revenueData?.revenueData?.yearlyRevenue || 0)}
                </h5>
                <p className="text-muted">Current Year Revenue</p>
                {revenueData?.revenueData?.growth > 0 && (
                  <div className="text-success font-size-12">
                    <i className="mdi mdi-menu-up font-size-14"></i>
                    {revenueData.revenueData.growth.toFixed(1)}% Growth
                  </div>
                )}
              </div>
            </Col>

            <Col sm={4}>
              <div className="text-center">
                <h5 className="mb-0 font-size-20">
                  {formatCurrency(
                    revenueData?.revenueData?.previousYearRevenue || 0
                  )}
                </h5>
                <p className="text-muted">Previous Year Revenue</p>
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xs={12}>
              <div className="text-center">
                <div className="d-inline-flex align-items-center">
                  <div className="d-flex align-items-center me-4">
                    <i className="mdi mdi-circle font-size-10 me-2 text-primary"></i>
                    <span>Current Year</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="mdi mdi-circle font-size-10 me-2 text-success"></i>
                    <span>Previous Year</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default RevenueAnalytics;
